import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEO from '../components/SEO';
import JsonLd, { breadcrumbSchema } from '../components/JsonLd';

const sections = [
  {
    heading: 'Privacy Policy',
    paragraphs: [
      'Our website https://imprint-mis.co.uk is committed to protecting your privacy. This privacy policy outlines how we collect, use, and safeguard your personal information when you visit our website and interact with our services.',
    ],
  },
  {
    heading: 'Information We Collect',
    paragraphs: [
      'We collect personal data when you voluntarily submit an inquiry through our contact form. The information we collect may include your name, email address, phone number, and any details related to your inquiry. This information is only used for the purpose of responding to your specific inquiry.',
      'We use the information collected from the contact form solely to respond to your inquiry. Your data will not be used for marketing purposes or shared with third parties, except as necessary to respond to your request.',
    ],
  },
  {
    heading: 'Google Analytics',
    paragraphs: [
      'Additionally, we use Google Analytics to track and analyse website traffic. Google Analytics collects data about your interactions with our website, such as your IP address, browser type, device information, and pages visited. This data helps us improve the user experience on our website. Please review Google\u2019s Privacy Policy for more information on how they collect and use data: https://policies.google.com/privacy',
      'Google Analytics data is used to analyse website traffic and improve user experience, but it does not identify individual users.',
      'We use Google Analytics, a web analytics service provided by Google, Inc. Google Analytics uses cookies to help us analyse how users interact with our website. The information generated by the cookie about your use of the website is transmitted to and stored by Google on servers in the United States. Please refer to Google\u2019s Privacy Policy for more information on how they collect and use data.',
    ],
  },
  {
    heading: 'Data Retention',
    paragraphs: [
      'We will retain your personal data only as long as necessary to fulfil the purpose for which it was collected or as required by law. For example, data from inquiries will be retained for a reasonable period of time to ensure we can respond and follow up appropriately.',
    ],
  },
  {
    heading: 'Cookies',
    paragraphs: [
      'Our website uses cookies to enhance your browsing experience. Cookies are small text files stored on your device that help us analyse website traffic, remember your preferences, and improve our services. By using our website, you consent to our use of cookies. You can manage your cookie preferences through your browser settings.',
    ],
  },
  {
    heading: 'Security',
    paragraphs: [
      'We take reasonable precautions to protect your personal data from unauthorised access, alteration, or destruction. While no method of transmission over the Internet is 100% secure, we strive to use commercially acceptable means to protect your information.',
    ],
  },
  {
    heading: 'Your Rights',
    paragraphs: [
      'Depending on your location, you may have certain rights regarding your personal data. These rights may include the right to access, update, or delete your information. If you wish to exercise any of these rights, please contact us at sales@imprint-mis.co.uk.',
    ],
  },
  {
    heading: 'Links to Other Websites',
    paragraphs: [
      'Our website may contain links to external websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party websites you visit.',
    ],
  },
  {
    heading: 'Changes to This Privacy Policy',
    paragraphs: [
      'We may update this privacy policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We will post any changes on this page and update the \u2018Last Updated\u2019 date at the bottom of the policy.',
    ],
  },
  {
    heading: 'Contact Us',
    paragraphs: [
      'If you have any questions about this privacy policy or our practices, please contact us at: sales@imprint-mis.co.uk',
    ],
  },
  {
    heading: 'Legal Basis for Processing (GDPR)',
    paragraphs: [
      'Under the General Data Protection Regulation (GDPR), we are required to inform you of the legal basis for collecting and processing your personal data. Our legal basis for processing your data is as follows:',
    ],
    bullets: [
      'Consent: When you submit an inquiry through our contact form, you are giving us explicit consent to use your personal information for the purpose of responding to your request.',
      'Legitimate Interests: We may process certain data for legitimate interests, such as improving our website, enhancing user experience, and providing relevant content. However, we ensure that your fundamental rights and freedoms are not overridden by our interests.',
    ],
    footer:
      'You have the right to withdraw your consent at any time by contacting us directly, and we will stop processing your data unless we have another lawful basis for retaining it.',
  },
  {
    heading: 'Data Transfers',
    paragraphs: [
      'We may transfer your personal data to countries outside of the European Economic Area (EEA), including the United States, for purposes such as website analytics through services like Google Analytics. When transferring your data, we take appropriate measures to ensure that it is protected in accordance with applicable data protection laws.',
      'In particular, Google Analytics uses Standard Contractual Clauses (SCCs) to safeguard your data when transferred internationally. These measures ensure that your personal data receives a level of protection that is consistent with the GDPR\u2019s requirements.',
      'By using our website, you acknowledge and consent to the transfer of your data as described in this section. If you have any concerns about these transfers, please feel free to contact us.',
    ],
  },
];

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="pt-0 lg:pt-28 pb-20 bg-white min-h-screen">
      <SEO
        title="Privacy Policy"
        description="How Imprint Business Systems collects, uses and safeguards your personal information."
        url="https://imprint-mis.co.uk/privacy-policy"
      />
      <JsonLd data={breadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Privacy Policy', path: '/privacy-policy' },
      ])} />

      {/* Hero */}
      <section className="bg-gray-50 border-b border-gray-100 py-16 lg:py-24">
        <div className="section-container">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#00BCE4] transition-colors mb-6"
          >
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <p
            className="text-[10px] font-semibold uppercase tracking-[3px] text-[#00BCE4] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Your Data, Protected
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="privacy-page-heading"
          >
            Privacy <span className="text-[#00BCE4]">Policy</span>
          </h1>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-20">
        <div className="section-container">
          <div className="max-w-3xl space-y-10" data-testid="privacy-policy-body">
            {sections.map((section) => (
              <div key={section.heading}>
                <h2
                  className="text-xl lg:text-2xl font-bold text-gray-900 mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {section.heading}
                </h2>
                <div
                  className="space-y-4 text-base text-gray-600 leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {section.paragraphs.map((p, i) => (
                    <p key={`${section.heading}-p-${i}`}>{p}</p>
                  ))}
                  {section.bullets && (
                    <ul className="list-disc pl-6 space-y-3">
                      {section.bullets.map((b, i) => (
                        <li key={`${section.heading}-b-${i}`}>{b}</li>
                      ))}
                    </ul>
                  )}
                  {section.footer && <p>{section.footer}</p>}
                </div>
              </div>
            ))}
            <p
              className="text-sm text-gray-400 italic pt-4 border-t border-gray-100"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Last Updated: 30-06-25
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
